import { useEffect, useState } from "react";

export interface DealUpdateConfirmDialogProps {
  dealName: string;
  /** Pre-formatted display rows: { field, from, to }. */
  changes: Array<{ field: string; from: string; to: string }>;
  /** Called when CONFIRM is clicked. Throw to surface inline error and stay open. */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Confirm dialog for chat-driven deal field writes. Same visual family + same
 * boundary rules as the existing WriteConfirm (Escape/CANCEL only — no
 * backdrop dismiss). Shows the explicit change grid the worker computed.
 */
export function DealUpdateConfirmDialog({
  dealName,
  changes,
  onConfirm,
  onCancel,
}: DealUpdateConfirmDialogProps) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const handleConfirm = async () => {
    setError(null);
    setRunning(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "write_failed");
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card">
        <div className="write-confirm-title">Update {dealName} in Zoho CRM</div>
        <div className="write-confirm-changes">
          {changes.map((ch, i) => (
            <div className="write-confirm-change" key={i}>
              <span className="write-confirm-change-label">{ch.field}</span>
              <span className="write-confirm-change-from">{ch.from}</span>
              <span className="write-confirm-change-arrow">→</span>
              <span className="write-confirm-change-to">{ch.to}</span>
            </div>
          ))}
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button className="write-confirm-cancel" onClick={onCancel} disabled={running}>
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleConfirm}
            disabled={running}
            autoFocus
          >
            {running ? "WRITING…" : "CONFIRM"}
          </button>
        </div>
      </div>
    </div>
  );
}
